import { statusLabel } from "../shared/ui";

/** Decorative glyphs from the supplied standalone/거래관리.dc.html. */
const categoryGlyphs: Readonly<Record<string, string>> = {
  RENT: "🏠",
  INSURANCE: "🛡️",
  BROKERAGE: "🤝",
  MAINTENANCE_FEE: "🏢",
  RENTAL: "🚗",
  EDUCATION: "🎓",
  OTHER: "📄",
};

export function transactionCategoryGlyph(code: string): string {
  return Object.prototype.hasOwnProperty.call(categoryGlyphs, code) ? categoryGlyphs[code]! : "📄";
}

export type TransactionManagementTone = "information" | "waiting" | "success" | "danger" | "neutral";
export interface TransactionManagementStatus {
  readonly label: string;
  readonly tone: TransactionManagementTone;
  readonly headline: string;
  readonly description: string;
}

type StatusCopy = Omit<TransactionManagementStatus, "label"> & { readonly label?: string };

/** Presentation only. These entries never authorize payment, cancellation or transfer. */
const statusCopy: Readonly<Record<string, StatusCopy>> = {
  REGISTERED: { tone: "information", headline: "거래가 등록됐어요", description: "등록한 거래와 제출한 자료를 확인하고 있어요." },
  UNDER_REVIEW: { tone: "information", headline: "심사가 진행 중이에요", description: "심사 결과가 나오면 안내해 드려요." },
  REVIEWING: { tone: "information", headline: "심사가 진행 중이에요", description: "심사 결과가 나오면 안내해 드려요." },
  SUPPLEMENT_REQUIRED: { label: "보완 요청", tone: "waiting", headline: "서류 보완이 필요해요", description: "요청 사항을 확인하고 서류를 다시 제출해 주세요." },
  PAYMENT_PENDING: { tone: "waiting", headline: "카드 결제를 기다리고 있어요", description: "심사가 승인됐어요. 카드로 결제해 주세요." },
  PAYMENT_PROCESSING: { tone: "information", headline: "결제 결과를 확인하고 있어요", description: "결제 결과가 확인될 때까지 기다려 주세요. 결제를 다시 요청하지 마세요." },
  PAYMENT_UNKNOWN: { tone: "information", headline: "결제 결과를 확인하고 있어요", description: "아직 결제 결과가 확정되지 않았어요. 결제 상태에서 확인해 주세요." },
  PAYMENT_CANCELING: { label: "결제 취소 확인 중", tone: "information", headline: "결제 취소 결과를 확인하고 있어요", description: "취소 결과가 확정될 때까지 송금하거나 결제를 다시 취소하지 마세요." },
  PAYMENT_FAILED: { tone: "danger", headline: "결제에 실패했어요", description: "결제 상태를 확인한 뒤 다시 진행해 주세요." },
  RETRY_PENDING: { tone: "waiting", headline: "결제 재시도를 기다리고 있어요", description: "결제 상태에서 현재 진행 상황을 확인해 주세요." },
  PAYMENT_EXPIRED: { tone: "neutral", headline: "결제 기한이 지났어요", description: "예약 한도가 해제되었습니다. 같은 거래가 필요하면 새로 접수해 주세요." },
  TRANSFER_SELECTION_PENDING: { label: "결제 완료", tone: "success", headline: "결제를 완료했어요.", description: "송금을 진행해 주세요." },
  TRANSFER_READY: { label: "송금 대기", tone: "information", headline: "송금을 기다리고 있어요", description: "선택한 송금 방식에 따라 진행돼요. 송금 상태를 확인해 주세요." },
  TRANSFER_PROCESSING: { tone: "information", headline: "송금을 진행하고 있어요", description: "처리 결과가 확정될 때까지 기다려 주세요." },
  TRANSFER_UNKNOWN: { tone: "information", headline: "송금 결과를 확인하고 있어요", description: "아직 송금 결과가 확정되지 않았어요. 송금 상태에서 확인해 주세요." },
  COMPLETED: { label: "거래 완료", tone: "success", headline: "거래가 완료됐어요", description: "카드 결제와 계좌 송금이 모두 끝났어요." },
  TRANSFER_FAILED: { tone: "danger", headline: "송금에 실패했어요", description: "송금 상태를 확인하고 고객센터로 문의해 주세요." },
  REJECTED: { label: "심사 반려", tone: "danger", headline: "거래가 어려워요", description: "심사 결과를 확인하고 고객센터로 문의해 주세요." },
  // A transaction can be canceled before card payment. Do not claim a card refund.
  CANCELED: { tone: "neutral", headline: "거래가 취소됐어요", description: "취소된 거래는 다시 진행할 수 없어요." },
  CLOSED: { tone: "neutral", headline: "거래가 종료됐어요", description: "거래 상태와 처리 내역을 확인해 주세요." },
};

export function transactionManagementStatus(status: string, paymentStatus?: string | null): TransactionManagementStatus {
  const copy = Object.prototype.hasOwnProperty.call(statusCopy, status) ? statusCopy[status] : undefined;
  if (!copy) return { label: "상태 확인 필요", tone: "neutral", headline: "거래 상태를 확인해 주세요", description: "현재 상태가 확인되지 않았어요. 고객센터로 문의해 주세요." };
  if (status === "CANCELED" && paymentStatus === "CANCELED") return { ...copy, label: "결제 취소", headline: "결제가 취소됐어요", description: "결제가 취소되어 이 거래는 다시 진행할 수 없어요." };
  return { ...copy, label: copy.label ?? statusLabel(status) };
}

/** The reference's MM.DD presentation, using the business time zone. */
export function transactionManagementDate(value: string): string {
  const normalized = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("ko-KR", { month: "2-digit", day: "2-digit", timeZone: "Asia/Seoul" }).formatToParts(parsed);
  return `${parts.find(part => part.type === "month")?.value}.${parts.find(part => part.type === "day")?.value}`;
}
